import React, { forwardRef, useEffect, useState, type InputHTMLAttributes } from 'react';

import { type Lucid } from '../../../types/types';
import Box from '../../box/box';
import Icon from '../../icon/icon';

export interface SearchBarProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
}

const inputStyle = (theme: Lucid.Theme.Brand) => ({
  flexGrow: 1,
  width: '100%',
  borderRadius: theme.radii.r8,
  padding: `${theme.space.s24}px 0 ${theme.space.s4}px 0`,
  border: 'none',
  color: theme.colors.coolGrey[100],
  fontSize: theme.fontSizes.fs16,
  scrollMarginTop: theme.space.s104,
  '&:focus': {
    outline: 'none',
  },
});

const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(
  ({ className, placeholder, onChange, defaultValue, ...rest }, forwardedRef) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isInputDirty, setIsInputDirty] = useState(false);
    const [shouldMinimizePlaceholder, setShouldMinimizePlaceholder] = useState(false);
    const [shouldDisplayPlaceholder, setShouldDisplayPlaceholder] = useState(false);

    useEffect(() => {
      setShouldMinimizePlaceholder(isFocused || isInputDirty || !!defaultValue);
      setShouldDisplayPlaceholder(true);
    }, [isFocused, isInputDirty, defaultValue]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) onChange(event);
      setIsInputDirty(!!event.target.value);
    };

    return (
      <label
        css={theme => ({
          label: 'searchBar',
          border: `1px solid ${theme.colors.coolGrey[80]}`,
          borderRadius: theme.radii.r4,
          display: 'grid',
          gridTemplateColumns: '50px 1fr',
          alignItems: 'center',
          justifyItems: 'center',
          width: '100%',
          backgroundColor: theme.colors.white,
          '&:hover': {
            borderColor: theme.colors.coolGrey[60],
          },
          [theme.breakpoints.bp900.min]: {
            width: 'auto',
          },
        })}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
      >
        <Icon color="coolGrey.60" type="MagnifyingGlass" />
        <Box
          css={theme => ({
            position: 'relative',
            width: '100%',
            height: theme.space.s48,
            fontSize: theme.fontSizes.fs16,
          })}
        >
          <span
            css={theme => [
              {
                position: 'absolute',
                fontSize: theme.fontSizes.fs16,
                top: theme.space.s16,
                left: 0,
                cursor: 'text',
                userSelect: 'none',
                pointerEvents: 'none',
                color: theme.colors.coolGrey[60],
                transition: 'font-size 0.3s ease, top 0.3s ease',
                display: 'none',
              },
              shouldMinimizePlaceholder && {
                fontSize: theme.fontSizes.fs12,
                top: '8px',
              },
              shouldDisplayPlaceholder && {
                display: 'block',
              },
            ]}
          >
            {placeholder}
          </span>
          <input
            ref={forwardedRef}
            css={theme => inputStyle(theme)}
            defaultValue={defaultValue}
            onBlur={() => setIsFocused(false)}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            type="text"
            {...rest}
            className={className}
          />
        </Box>
      </label>
    );
  },
);

export { SearchBar };
export default SearchBar;
